/**
 * How the installed azure-kusto-data (7.2.0, `connectionBuilderBase.js`) reads a Kusto connection string — the text the
 * app hands `KustoConnectionStringBuilder.withTokenCredential(normalizeKustoClusterUrl(server), …)` is one (security-slice
 * review round 4, Codex R4-1), and the New Connection dialog's paste reads a pasted Kusto string the same way (Option C
 * C3 review r3, C3-16): a trailing `/` or `\` dropped, `Data Source=` prepended when the first `;`-item has no `=`, then
 * split on `;` and each item on `=` (the value is the second piece, trimmed — no quoting, so a `"` inside a password does
 * not protect a `;`), keywords matched case-insensitively, a later alias of a property overriding an earlier one, an
 * unknown keyword or a flag with no value an error. A mirror, not an import: the SDK is ESM, loaded lazily by the Kusto
 * manager, and both callers are synchronous; `sealedTargets.identity.test.ts` holds the mirror to the installed SDK over
 * a corpus. Pure and process-agnostic (moved from `electron/connection-transfer/` so the renderer reads it too).
 */

type Keyword = { names: readonly string[]; isBool?: true; isSecret?: true };

/** The SDK's `KeywordMapping`: property → accepted names, boolean flag, secret flag. */
export const KUSTO_KEYWORDS: Readonly<Record<string, Keyword>> = {
  dataSource: { names: ['data source', 'addr', 'address', 'network address', 'server'] },
  aadFederatedSecurity: { names: ['aad federated security', 'federated security', 'federated', 'fed', 'aadfed'], isBool: true },
  initialCatalog: { names: ['initial catalog', 'database'] },
  aadUserId: { names: ['aad user id'] },
  password: { names: ['password', 'pwd'], isSecret: true },
  applicationClientId: { names: ['application client id', 'appclientid'] },
  applicationKey: { names: ['application key', 'appkey'], isSecret: true },
  applicationCertificatePrivateKey: { names: ['application certificate privatekey'], isSecret: true },
  applicationCertificateSendX5c: {
    names: ['application certificate x5c', 'application certificate send public certificate', 'application certificate sendx5c', 'sendx5c'],
    isBool: true,
  },
  authorityId: { names: ['authority id', 'authorityid', 'authority', 'tenantid', 'tenant', 'tid'] },
};

const DEFAULT_DATABASE = 'NetDefaultDB';

/** The SDK property a keyword names (case-insensitive, trimmed), or undefined for a keyword the SDK does not know. */
export function kustoPropertyOf(key: string): string | undefined {
  const name = key.trim().toLowerCase();
  return Object.keys(KUSTO_KEYWORDS).find((property) => KUSTO_KEYWORDS[property].names.includes(name));
}

/** One `;`-item as the SDK splits it: its text, the keyword (`pieces[0]`), its value (`pieces[1]`, untrimmed), the piece count. */
export type KustoItem = { text: string; key: string; property: string | undefined; value: string | undefined; pieces: number };

/** The SDK's items for `connectionString`, in order, after its trailing-slash and `Data Source=` rewrites. */
export function kustoConnectionItems(connectionString: string): KustoItem[] {
  let text = connectionString;
  if (text.endsWith('/') || text.endsWith('\\')) text = text.slice(0, -1);
  if (text && !text.split(';')[0].includes('=')) text = `Data Source=${text}`;
  return text.split(';').map((item) => {
    const pieces = item.split('=');
    return { text: item, key: pieces[0], property: kustoPropertyOf(pieces[0]), value: pieces[1], pieces: pieces.length };
  });
}

/** The builder's properties for this connection string, as the SDK sets them; undefined where the SDK throws. */
export function kustoBuilderProperties(connectionString: string): Record<string, unknown> | undefined {
  if (connectionString.trim().length === 0) return undefined;
  const out: Record<string, unknown> = { authorityId: 'organizations' };
  for (const item of kustoConnectionItems(connectionString)) {
    if (!item.property) return undefined;
    if (KUSTO_KEYWORDS[item.property].isBool) {
      if (item.value === undefined) return undefined;
      out[item.property] = item.value.trim().toLowerCase() === 'true';
    } else {
      out[item.property] = item.value?.trim();
    }
  }
  if (!out.initialCatalog) out.initialCatalog = DEFAULT_DATABASE;
  return JSON.parse(JSON.stringify(out)) as Record<string, unknown>;
}
